import { Box, Button, Chip, CircularProgress, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NamingCandidate, NamingWizardStep } from './useProductNameSuggestionWizard';

interface NamingQuestionStepProps {
  step: NamingWizardStep;
  isLoading: boolean;
  onSelectCandidate: (candidate: NamingCandidate) => void;
  onBack?: () => void;
}

export default function NamingQuestionStep({
  step,
  isLoading,
  onSelectCandidate,
  onBack,
}: NamingQuestionStepProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'products']);
  const [customText, setCustomText] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string>();
  const notApplicableLabel = t('products:naming_wizard.not_applicable');
  const hasCandidates = step.candidates.length > 0;
  const [isCustom, setIsCustom] = useState(!hasCandidates);
  const isSkipping = isLoading && selectedLabel === notApplicableLabel;
  const cancelCustom = (): void => {
    setIsCustom(false);
    setCustomText('');
  };
  const selectCandidate = (candidate: NamingCandidate): void => {
    setSelectedLabel(candidate.displayValue);
    onSelectCandidate(candidate);
  };
  const skip = (): void => {
    selectCandidate({ displayValue: notApplicableLabel, value: '' });
  };
  const confirmCustom = (): void => {
    const value = customText.trim();

    if (value && !isLoading) {
      selectCandidate({ displayValue: value, value });
    }
  };
  const renderChip = (label: string, onClick: () => void): React.JSX.Element => (
    <Chip
      variant="assistive"
      key={label}
      label={label}
      disabled={isLoading && selectedLabel !== label}
      icon={isLoading && selectedLabel === label ? <CircularProgress size={16} /> : undefined}
      onClick={isLoading ? undefined : onClick}
    />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="titleMedium">{step.question}</Typography>
      {isCustom ? (
        <TextField
          fullWidth
          autoFocus
          variant="outlined"
          autoComplete="off"
          value={customText}
          onChange={(event) => setCustomText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              confirmCustom();
            }
          }}
          disabled={isLoading}
          placeholder={step.placeholder || t('products:naming_wizard.custom_placeholder')}
          type={step.isNumeric ? 'number' : 'text'}
        />
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {step.candidates.map((candidate) => renderChip(candidate.displayValue, () => selectCandidate(candidate)))}
          {renderChip(t('products:naming_wizard.other'), () => setIsCustom(true))}
        </Box>
      )}
      {(isCustom || onBack || step.isOptional) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1.5 }}>
          {isCustom && hasCandidates && (
            <Button variant="text" disabled={isLoading} onClick={cancelCustom}>
              {t('common:cancel')}
            </Button>
          )}
          {onBack && !(isCustom && hasCandidates) && (
            <Button variant="outlined" disabled={isLoading} onClick={onBack}>
              {t('common:previous')}
            </Button>
          )}
          {step.isOptional && (
            <Button variant="outlined" loading={isSkipping} disabled={isLoading} onClick={skip}>
              {notApplicableLabel}
            </Button>
          )}
          {isCustom && (
            <Button variant="contained" loading={isLoading && !isSkipping} disabled={!customText.trim()} onClick={confirmCustom}>
              {t('common:confirm')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
